/**
 * Application Roles Constants
 *
 * Centralized role definitions to ensure consistency across the application.
 * Use these constants instead of hardcoded strings throughout the codebase.
 */

export const ROLES = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  HR_ADMIN: 'HR Admin',
  HR_ASSISTANT: 'HR Assistant',
  DIRECTOR: 'Director',
  MANAGER: 'Manager',
  HOD: 'HOD', // Head of Department
  EMPLOYEE: 'Employee',
};

// Role groups for common access patterns
export const ROLE_GROUPS = {
  // Full administrative access
  SUPER_ADMINS: [ROLES.SUPER_ADMIN],

  // HR department roles
  HR_ROLES: [ROLES.HR_ADMIN, ROLES.HR_ASSISTANT],

  // Management roles
  MANAGEMENT_ROLES: [ROLES.DIRECTOR, ROLES.MANAGER, ROLES.HOD],

  // All administrative roles (can manage employees, see reports, etc.)
  ADMIN_ROLES: [
    ROLES.SUPER_ADMIN,
    ROLES.ADMIN,
    ROLES.HR_ADMIN,
    ROLES.HR_ASSISTANT,
    ROLES.DIRECTOR,
    ROLES.MANAGER,
    ROLES.HOD,
  ],

  // Roles that can manage employees
  EMPLOYEE_MANAGERS: [
    ROLES.SUPER_ADMIN,
    ROLES.ADMIN,
    ROLES.HR_ADMIN,
    ROLES.HR_ASSISTANT,
    ROLES.MANAGER,
    ROLES.HOD,
  ],

  // Roles that bypass branch filter (can see all branches)
  BRANCH_ADMINS: [ROLES.HR_ADMIN, ROLES.ADMIN, ROLES.HR_ASSISTANT, ROLES.SUPER_ADMIN],

  // Roles that can approve/reject requests
  APPROVERS: [
    ROLES.SUPER_ADMIN,
    ROLES.HR_ADMIN,
    ROLES.HR_ASSISTANT,
    ROLES.MANAGER,
    ROLES.HOD,
  ],

  // Roles that can view reports
  REPORT_VIEWERS: [
    ROLES.SUPER_ADMIN,
    ROLES.HR_ADMIN,
    ROLES.HR_ASSISTANT,
    ROLES.DIRECTOR,
    ROLES.MANAGER,
    ROLES.HOD,
  ],

  // All roles
  ALL_ROLES: [
    ROLES.SUPER_ADMIN,
    ROLES.ADMIN,
    ROLES.HR_ADMIN,
    ROLES.HR_ASSISTANT,
    ROLES.DIRECTOR,
    ROLES.MANAGER,
    ROLES.HOD,
    ROLES.EMPLOYEE,
  ],
};

/**
 * Helper function to check if a role is in a group
 * @param {string} role - The role to check
 * @param {string[]} group - The group of roles to check against
 * @returns {boolean}
 */
export const isRoleInGroup = (role, group) => {
  return group.includes(role);
};

/**
 * Helper function to check if user is an admin (can bypass branch filter)
 * @param {string} roleName - The role name to check
 * @returns {boolean}
 */
export const isBranchAdmin = (roleName) => {
  return ROLE_GROUPS.BRANCH_ADMINS.includes(roleName);
};

/**
 * Helper function to check if user is HOD
 * @param {string} roleName - The role name to check
 * @returns {boolean}
 */
export const isHOD = (roleName) => {
  return roleName === ROLES.HOD;
};

/**
 * Helper function to check if user is Manager
 * @param {string} roleName - The role name to check
 * @returns {boolean}
 */
export const isManager = (roleName) => {
  return roleName === ROLES.MANAGER;
};

/**
 * Helper function to check if user is Employee only
 * @param {string} roleName - The role name to check
 * @returns {boolean}
 */
export const isEmployee = (roleName) => {
  return roleName === ROLES.EMPLOYEE;
};

/**
 * Helper function to check if user can approve requests
 * @param {string} roleName - The role name to check
 * @returns {boolean}
 */
export const canApprove = (roleName) => {
  return ROLE_GROUPS.APPROVERS.includes(roleName);
};

export default ROLES;
